import { useState } from "react";
import {
  Search,
  FolderKanban,
  Wallet,
  Package,
  Users2,
  CheckSquare,
  Settings,
  ThumbsUp,
  ThumbsDown,
  ArrowLeft,
  HardHat,
  ClipboardList,
  Building2,
  ShoppingCart,
} from "lucide-react";

const categories = [
  // ─────────────────────────────────────────────
  // 1. PROJECTS
  // ─────────────────────────────────────────────
  {
    id: "projects",
    title: "Projects & Sites",
    description: "Create and manage construction projects and sites",
    icon: FolderKanban,
    colorClass: "bg-purple-100/80 border-purple-200",
    articles: [
      {
        id: "create-project",
        title: "How to Create a New Project / Site",
        summary:
          "Set up a new construction site in SiteMaster with building category, construction status, and optional photos.",
        steps: [
          "Navigate to the Projects section from the left sidebar",
          "Click the + New Project button located at the top right of the dashboard",
          "In the New Site window, enter the Site Name",
          "Select the appropriate Local Administrative Body (Municipality, Panchayat, or Other) and click Next",
          "Select the relevant Building Category (Residential Building, Commercial Building, Institutional Building, or Mixed Use Building) and click Next",
          "Under Construction Status, enter the Starting Date and set the current Status (e.g., Ongoing)",
          "(Optional) Click Upload Photos to add site photos",
          "Click the Create button to finalize the setup",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 2. TASKS
  // ─────────────────────────────────────────────
  {
    id: "tasks",
    title: "Tasks & Subtasks",
    description: "Manage task groups, tasks, and subtasks within projects",
    icon: ClipboardList,
    colorClass: "bg-violet-100/80 border-violet-200",
    articles: [
      {
        id: "task-groups",
        title: "Managing Task Groups",
        summary: "Learn how to update and manage your high-level task groups.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner,Admin, Project Admin, or individuals with specific 'Task Authority' access can create, edit, or delete Task Groups",
          "Edit Name: Click directly on the Task Group's name to edit it",
          "Delete: Click the three-dot menu icon in the top right of the task group panel",
          "Select Delete to remove the task group entirely",
        ],
        tips: [
          "Task Authority access must be explicitly granted in Organization Team settings",
        ],
      },
      {
        id: "manage-tasks",
        title: "Managing Tasks",
        summary:
          "Learn how to configure details for individual tasks including assignees, dates, and updates.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner, Admin, Project Admin, or individuals with specific 'Task Authority' access can rename tasks, manage assignees, and set dates",
          "Auto-Assignment: Owners and Admins are automatically added to tasks as Task Assignees",
          "Assignee Restrictions: Task Assignees can only add comments and upload files/images in the Updates tab — they cannot add/delete assignees, select dates, or add main task files",
          "Rename: Click the three-dot menu located within the task panel to edit the task's name",
          "Assign Members: Add specific team members as assignees to the task",
          "Set Dates: Select a start and end date for the overall task timeline",
          "Updates & Files: Navigate to the Updates section to write messages and upload relevant files or attachments",
        ],
        tips: [
          "Assignees have limited access — they can only add comments and upload files in the Updates tab",
        ],
      },
      {
        id: "manage-subtasks",
        title: "Managing Subtasks",
        summary:
          "Learn how to detail, assign, and complete sub-level action items.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner, Admin, Project Admin, or individuals with specific 'Task Authority' access can edit subtask details, manage assignees, and set dates",
          "Auto-Assignment: Owners and Admins are automatically added to subtasks as Subtask Approvers",
          "Assignee Restrictions: Subtask Assignees can only add comments, upload files, and send approval requests — they cannot add/delete assignees, select dates, or add main subtask files",
          "Edit Details: Open the subtask and click the document info icon at the top — enter a new name or descriptive summary, then click Save Changes",
          "Add Assignee: Designate a team member — Important: You must add Task Assignees first; only members already assigned to the parent Task can be selected as a Subtask Assignee",
          "Set Dates: Select the date range to set the subtask's specific timeline",
          "Assign Approvers: Select a reviewer to approve the work — only members assigned to the parent task can be set as approvers",
          "Request Approval: Subtask Assignees can write an update message, attach relevant files in the Updates tab, and click the Request Approval button to notify the designated approver",
          "Complete Subtask: Click the circle checkmark icon at the top right of the panel to mark the subtask as completed",
        ],
        tips: [
          "You must add Task Assignees before assigning anyone to a subtask",
          "Owners and Admins are automatically added as Subtask Approvers",
          "Only Task Assignees can be set as Subtask Approvers",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 3. FINANCE
  // ─────────────────────────────────────────────
  {
    id: "finance",
    title: "Finance & Transactions",
    description: "Track project expenses, payments, and financial records",
    icon: Wallet,
    colorClass: "bg-orange-100/80 border-orange-200",
    articles: [
      {
        id: "add-transaction",
        title: "Adding Transactions",
        summary:
          "Log various types of financial activity including payments, refunds, and receipts.",
        steps: [
          "PERMISSIONS — Full Access: The Owner, Admin, Project Admin, and individuals explicitly granted 'Edit' access to the Finance section can add payments, update pending transactions, and delete records",
          "View Only: Individuals with only 'View' access can see the financial dashboard and transaction history but cannot make any changes or execute new transactions",
          "Navigate to the Finance section from your project's left-hand sidebar to log new financial activity",
          "Other Payment: Select 'Other Payment' to record miscellaneous project expenses — enter the amount, payment method, and details, then save",
          "Refund Payment: Choose 'Refund Payment' to log any money returned to the project — fill in the refunded amount, source, and date, then finalize the entry",
          "Receipt Payment: Select 'Receipt Payment' to document incoming funds or payments received — input the received amount and related details, then save",
          "Worker Payment: Choose 'Worker Payment' to process team wages — select the specific worker, enter the paid amount, and confirm the transaction",
        ],
        tips: [
          "View Only users can see records but cannot make changes or execute transactions",
        ],
      },
      {
        id: "update-transaction",
        title: "Updating Pending Transactions",
        summary:
          "Modify the status or details of a pending financial transaction.",
        steps: [
          "Locate the specific pending transaction within your Finance list",
          "Click on or edit the transaction to open its details",
          "Update the payment status (e.g., changing it from Pending to Completed) or modify the payment information as needed",
          "Save the changes",
        ],
      },
      {
        id: "delete-transaction",
        title: "Deleting Transactions",
        summary:
          "Permanently remove an incorrect or unwanted financial record.",
        steps: [
          "Find the transaction you need to remove from your financial records",
          "Click the delete option (usually a trash can icon or found within a three-dot menu) next to the transaction",
          "Confirm the prompt to permanently delete the record from the system",
        ],
        tips: [
          "Deletion is permanent and cannot be undone — confirm carefully",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 4. WORKERS
  // ─────────────────────────────────────────────
  {
    id: "workers",
    title: "Workers & Attendance",
    description: "Onboard workers, track attendance, and manage payments",
    icon: Users2,
    colorClass: "bg-green-100/80 border-green-200",
    articles: [
      {
        id: "add-worker",
        title: "Adding a New Worker",
        summary: "Onboard a new worker with their role and payment details.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner, Admin, Project Admin, or individuals explicitly granted 'Edit' access to the Workers section can add workers, edit details, process payments, and mark attendance",
          "View Only: Individuals with only 'View' access can see the dashboard and worker details but cannot make any changes or execute transactions",
          "To add a team member, open the New Worker window",
          "Under General Details: enter the worker's name, select their field of work (e.g., Carpentry), and provide their contact number and optional address — click Next",
          "Under Payment Details: select the payment type (e.g., Daily wage), enter the amount, and click Save",
        ],
      },
      {
        id: "mark-attendance",
        title: "Marking Attendance",
        summary:
          "Log daily attendance for workers including full day, half day, and overtime shifts.",
        steps: [
          "Click the Mark Attendance button located on the Workers dashboard",
          "Select individual workers by checking the boxes next to their names, or use the Select all checkbox",
          "Ensure the status is set to Present and select the shift type: Full day, Half day, or Overtime",
          "Special Condition: If you select Overtime, you must manually input the Overtime Hours and Overtime Wage",
          "Click the Mark Attendance button at the bottom of the window to log the records",
        ],
        tips: [
          "Overtime requires manual entry of both Overtime Hours and Overtime Wage before you can save",
        ],
      },
      {
        id: "worker-payments",
        title: "Processing Worker Payments",
        summary:
          "Pay individual workers or process bulk payments for the entire team.",
        steps: [
          "Click the + Transaction button to open the Worker Payment window",
          "Single Payments: Select a specific worker from the dropdown to view their Payment Summary",
          "Customize the payment by entering a partial or full sum into the Paid Amount field",
          "Select a Payment Method (Cash, Online, or Cheque), add optional remarks or upload a payment slip, and click Save",
          "Multi-Worker Payments: Click the Multi-Worker Payment toggle at the top right of the window",
          "Important Condition: When paying multiple workers at once, you must pay the exact total pending amount shown — no partial payments are allowed",
        ],
        tips: [
          "Single payments allow partial amounts; multi-worker payments require the exact full pending amount",
        ],
      },
      {
        id: "edit-delete-worker",
        title: "Editing and Deleting Workers",
        summary: "Update worker wage rates or remove workers from the system.",
        steps: [
          "Click directly on a worker's row in the dashboard to open their detailed profile — here you can view Attendance Analytics and Finance History",
          "Edit Wage: Click the Edit Daily Rate button to update their payment terms",
          "Delete Worker: Click the red trash can icon in the top right corner and confirm the deletion prompt to remove the worker from the system",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 5. PROJECT REPORTS
  // ─────────────────────────────────────────────
  {
    id: "reports",
    title: "Project Reports",
    description: "Generate customized PDF or Excel project activity reports",
    icon: ClipboardList,
    colorClass: "bg-cyan-100/80 border-cyan-200",
    articles: [
      {
        id: "generate-report",
        title: "Generating Project Reports",
        summary:
          "Create customized, detailed reports of your project's activities over any given time frame.",
        steps: [
          "Navigate to the Reports (or Project Report) section from your project dashboard",
          "Select Time Period: Choose a predefined range by clicking Today, 7 Days, or 1 Month",
          "Alternatively, select Specific to choose a custom date range using the From and To calendar pickers",
          "Choose Export Format: Select whether you want the final document to be a PDF or an Excel file",
          "Select Report Sections: Customize the content by checking or unchecking boxes — available sections include: Site Updates, Payments & Transactions, Attendance, Material Inventory, Material Deliveries, and Tasks",
          "Click the Generate Report button at the bottom of the page to compile and download your file",
        ],
        tips: [
          "You can include or exclude any combination of sections to tailor the report to your needs",
          "Both PDF and Excel formats are available for export",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 6. SNAGS
  // ─────────────────────────────────────────────
  {
    id: "snags",
    title: "Snags & Communications",
    description: "General updates, issue chatrooms, and file sharing",
    icon: CheckSquare,
    colorClass: "bg-pink-100/80 border-pink-200",
    articles: [
      {
        id: "general-updates",
        title: "Sending General Updates",
        summary:
          "The General Updates channel is an open forum where any team member can share broad project news, progress, or general queries.",
        steps: [
          "Navigate to Snags: Select Snags from the project's left-hand sidebar",
          "Send Messages: Click on General Updates — type your message in the text box at the bottom and press Enter or the send arrow",
          "Share Files: Use the paperclip icon to attach images or documents related to the construction work",
          "Send Audio: Click the microphone icon to record and send voice messages directly to the chat",
        ],
        tips: [
          "Any team member can post in General Updates — it is an open forum for the whole project team",
        ],
      },
      {
        id: "snag-list",
        title: "Creating & Managing the Snag List",
        summary:
          "The Snag List allows you to create targeted, private chatrooms for specific tasks, issues, or subgroups within the project.",
        steps: [
          "Create a New Snag: Click the + (plus) icon next to the Snag List header",
          "Enter a title for your new chatroom (e.g., 'making documentation') and click Create",
          "Add Members: A window will immediately prompt you to select members — check the boxes next to the individuals you want to include and click Save",
          "Interact in the Snag: Just like in General Updates, members can type messages, upload files, and send voice notes",
          "Edit a Snag Name: Hover over an existing snag in your list and click the pencil icon — type the new name and click Save",
          "Delete a Snag: Click the red trash can icon next to the snag you want to remove — click Delete on the confirmation prompt to permanently erase the chatroom",
        ],
        tips: [
          "Snag chatrooms are private — only members you add during creation can see and interact in them",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 7. MATERIALS
  // ─────────────────────────────────────────────
  {
    id: "materials",
    title: "Materials",
    description:
      "Inventory, material requests, and purchase & delivery tracking",
    icon: Package,
    colorClass: "bg-blue-100/80 border-blue-200",
    articles: [
      {
        id: "material-inventory",
        title: "Material Inventory",
        summary:
          "Manage your site's inventory and track current quantities of materials available on-site.",
        steps: [
          "PERMISSIONS — Full Access: The Owner, Admin, Project Admin, and individuals with 'Edit' access to the Materials section can create material requests, add materials to the inventory, and manage purchases and deliveries",
          "View Only: Individuals with only 'View' access can see the inventory, requests, and delivery statuses but cannot make any changes or additions",
          "Navigate to the Materials section within your project dashboard to view all currently available stock",
          "Use this section to manually add new materials to your site's inventory and track current quantities",
        ],
      },
      {
        id: "material-requests",
        title: "Material Requests",
        summary:
          "Generate a new request for materials required on-site, which is sent to organizational Procurement for approval.",
        steps: [
          "Navigate to the Materials section within your project",
          "Go to the Material Requests tab",
          "Generate a new request for materials required on-site",
          "The request is sent to the organizational Procurement section for approval and purchasing",
        ],
      },
      {
        id: "purchase-delivery",
        title: "Purchase & Delivery",
        summary:
          "Track approved purchases, receive materials on-site, and return faulty or mismatched deliveries.",
        steps: [
          "Navigate to the Materials section from your project sidebar and access the Purchase & Delivery tab to track the status of approved purchases",
          "Receive Material: When an order arrives on-site, log the received items in this section — this will automatically update your project's active Material Inventory",
          "Return Material: If the delivered material is faulty, damaged, or mismatched, select the specific delivery and use the Return option to send the items back and keep your inventory records accurate",
        ],
        tips: [
          "Receiving materials automatically updates your Material Inventory — no manual entry needed",
          "Use the Return option to keep inventory records accurate for faulty or mismatched deliveries",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 8. PROCUREMENT
  // ─────────────────────────────────────────────
  {
    id: "procurement",
    title: "Procurement",
    description: "Material library, RFQs, purchase orders, and vendor quotes",
    icon: ShoppingCart,
    colorClass: "bg-indigo-100/80 border-indigo-200",
    articles: [
      {
        id: "procurement-overview",
        title: "Procurement Permissions & Overview",
        summary:
          "Centrally manage material libraries, process site requests, gather vendor quotations, and issue Purchase Orders.",
        steps: [
          "PERMISSIONS — Full Access: Owners, Admins, and individuals granted access to both 'Procurement' and 'Vendor' sections can perform all actions, including filling vendor quotes and sending WhatsApp messages",
          "Restricted Procurement Access: Individuals with access to only the 'Procurement' section can access the module, create materials, view requests, add new vendors, proceed to Purchase Orders, or reject requests",
          "Special Condition: Restricted users cannot use the 'fill for vendors' action or send WhatsApp quotations",
        ],
        tips: [
          "Both 'Procurement' AND 'Vendor' access are required to fill vendor quotes and send WhatsApp quotations",
        ],
      },
      {
        id: "direct-creation",
        title: "Direct Creation Actions",
        summary:
          "Bypass the standard workflow and directly create Material Requests, RFQs, or Purchase Orders.",
        steps: [
          "Directly Create a Material Request: Navigate to the Material Requests section and use the create button to manually log a new request on behalf of a site",
          "Directly Raise for Quotation (RFQ): Go to the RFQ section and create a new quotation request from scratch to immediately solicit vendor pricing",
          "Directly Create a Purchase Order (PO): Open the Purchase Orders section and draft a new PO directly when vendor details and pricing are already confirmed, bypassing the RFQ stage",
        ],
        tips: [
          "Use Direct Creation to expedite purchasing when the full workflow is not needed",
        ],
      },
      {
        id: "material-library",
        title: "Step 1 – Material Library",
        summary:
          "Add and define materials that are available for requests and purchasing across the organization.",
        steps: [
          "Important: Materials must be created here before they can be requested or purchased in the standard flow",
          "Navigate to the Material Library within Procurement",
          "Add and define new materials — enter the name, unit of measurement, and specifications",
          "Once saved, materials are available across the entire organization",
        ],
        tips: [
          "Always create materials in the Material Library first — they cannot be requested without being defined here",
        ],
      },
      {
        id: "managing-material-requests",
        title: "Step 2 – Managing Material Requests",
        summary:
          "View and review all consolidated material requests submitted from various project sites.",
        steps: [
          "View all consolidated Material Requests submitted from various project sites or created directly",
          "Review the requested quantities and specifications",
          "Initiate the purchasing process by proceeding to the RFQ stage",
        ],
      },
      {
        id: "rfq",
        title: "Step 3 – Request for Quotation (RFQ)",
        summary:
          "Convert a Material Request into an RFQ to solicit pricing from suppliers.",
        steps: [
          "Convert a Material Request into an RFQ to solicit pricing from suppliers",
          "Add Vendors: Select existing vendors or add a new vendor to the RFQ",
          "Fill for Vendors: Manually input the pricing and quotation details received from vendors (requires Vendor access)",
          "Send WhatsApp Quotation: Send the RFQ directly to the vendor's WhatsApp for a quick response (requires Vendor access)",
          "Decision: Review the quotes and choose to either Reject the quotation or Proceed to PO with the winning bid",
        ],
        tips: [
          "Filling vendor quotes and sending WhatsApp quotations requires both Procurement and Vendor access",
        ],
      },
      {
        id: "purchase-orders",
        title: "Step 4 – Purchase Orders (PO)",
        summary:
          "Generate and issue formal Purchase Orders to finalize purchases with vendors.",
        steps: [
          "Once a quotation is approved (or if creating directly), generate a formal Purchase Order",
          "Issue the PO to the selected vendor to finalize the purchase",
          "This triggers the delivery phase to the project site",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 9. VENDORS
  // ─────────────────────────────────────────────
  {
    id: "vendors",
    title: "Vendor Management",
    description: "Manage suppliers, advance payments, and direct purchases",
    icon: Building2,
    colorClass: "bg-amber-100/80 border-amber-200",
    articles: [
      {
        id: "vendor-permissions",
        title: "Vendor Permissions & Access",
        summary:
          "Understand who can manage vendors and what actions are available.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner, Admin, and individuals within the organization team explicitly granted 'Vendor Management' access can view, create, manage, and delete vendor details and transactions",
        ],
        tips: [
          "'Vendor Management' access must be explicitly granted in the Organization Team settings",
        ],
      },
      {
        id: "create-vendor",
        title: "Creating a New Vendor",
        summary: "Add a new supplier to your organization's vendor directory.",
        steps: [
          "Navigate to the Vendor section from the organization's left-hand sidebar",
          "Click the button to add a new vendor (usually a + Add Vendor or New button)",
          "Enter the vendor's essential details: business name, contact information, and the materials/services they supply",
          "Save the profile",
        ],
      },
      {
        id: "delete-vendor",
        title: "Deleting a Vendor",
        summary:
          "Permanently remove a vendor from your organization's records.",
        steps: [
          "Locate the specific vendor in your directory list",
          "Click the delete icon (typically a trash can) or select the delete option from the three-dot menu next to the vendor's name",
          "Confirm the prompt to permanently remove the vendor from your organization's records",
        ],
      },
      {
        id: "vendor-advance-payment",
        title: "Processing an Advance Payment",
        summary: "Log an advance payment to a vendor from their profile.",
        steps: [
          "Select the specific vendor from your list to open their profile",
          "Navigate to their payment or transaction tab and select Advance Payment",
          "Enter the advance amount",
          "Choose the payment method: Cash, Online, or Cheque",
          "Add any optional remarks or payment slips",
          "Click Save to log the transaction",
        ],
      },
      {
        id: "direct-purchase",
        title: "Making a Direct Purchase",
        summary:
          "Quickly log a purchase made directly from a vendor, bypassing the standard RFQ flow.",
        steps: [
          "Use the Direct Purchase action on the vendor's profile to log a purchase bypassing the standard Material Request and RFQ flow",
          "Fill in the specific details of the transaction: items purchased, total cost, and date",
          "Finalize the record to update your vendor's ledger",
        ],
        tips: [
          "Direct Purchase is useful for ad-hoc or urgent purchases where the full RFQ process is not needed",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 10. CONTRACTORS
  // ─────────────────────────────────────────────
  {
    id: "contractors",
    title: "Contractors",
    description: "Contractor profiles, work orders, attendance, and payments",
    icon: HardHat,
    colorClass: "bg-yellow-100/80 border-yellow-200",
    articles: [
      {
        id: "contractor-permissions",
        title: "Contractor Permissions & Access",
        summary: "Understand who can manage contractors and what they can do.",
        steps: [
          "PERMISSIONS — Full Access: Only the Owner, Admin, and individuals explicitly granted 'Manage Contractor' access can create, edit, and delete contractors, create work orders, mark attendance, and make payments",
          "View Only: Individuals without 'Manage Contractor' access can only view contractor details and cannot make any changes",
        ],
      },
      {
        id: "contractor-profiles",
        title: "Managing Contractor Profiles",
        summary: "Create, edit, and delete contractor profiles in the system.",
        steps: [
          "Create Contractor: Navigate to the Contractor section from the sidebar and click the option to add a new contractor — enter the required details and save",
          "Edit Contractor: Select an existing contractor's profile and click the edit option to update their information",
          "Delete Contractor: Locate the contractor you want to remove, select the delete option, and confirm to remove them from the system",
        ],
      },
      {
        id: "work-orders",
        title: "Creating Work Orders",
        summary:
          "Set up work orders with milestone, monthly, or fixed price payment structures.",
        steps: [
          "Open a contractor's profile and click the Add Work Order button",
          "Select the appropriate payment structure for the agreement:",
          "Milestone: Payments are based on the completion of specific project stages",
          "Monthly Payment: A recurring, fixed monthly payment",
          "Fixed Price (Lumpsum): A single, fixed total payment for the entire job",
          "Fill in the specific scope, deadlines, and financial amounts",
          "Save the work order",
        ],
        tips: [
          "Choose the payment structure carefully — Milestone for stage-based work, Monthly for ongoing work, Lumpsum for fixed-scope jobs",
        ],
      },
      {
        id: "contractor-attendance",
        title: "Tracking Contractor Attendance",
        summary:
          "Record contractor presence and active days on the project site.",
        steps: [
          "Open the contractor's profile",
          "Navigate to their Attendance section",
          "Log their attendance to record their active days or presence on the site",
        ],
      },
      {
        id: "contractor-payments",
        title: "Processing Contractor Payments",
        summary:
          "Process payments for a contractor against their active work orders.",
        steps: [
          "Navigate to the contractor's payment or finance section",
          "Select the relevant work order",
          "Enter the payment amount",
          "Choose the payment method",
          "Complete the transaction",
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 11. ORGANIZATION TEAM
  // ─────────────────────────────────────────────
  {
    id: "team",
    title: "Organization Team",
    description:
      "Invite members and assign organization or project-level access",
    icon: Settings,
    colorClass: "bg-teal-100/80 border-teal-200",
    articles: [
      {
        id: "team-permissions",
        title: "Team Access Levels Overview",
        summary:
          "Understand the different access levels available for organization and project members.",
        steps: [
          "PERMISSIONS — Full Access (Admin): Users assigned the Admin role at the Organization Level have full Admin privileges — they can access and modify every section, project, and setting within the organization",
          "Project Admin: Users assigned the Admin role for a specific project can manage all sections and details only within that designated project",
        ],
        tips: [
          "Organization-level Admin = Admin with full platform access",
          "Project Admin = full control only within that specific project",
        ],
      },
      {
        id: "invite-member",
        title: "Inviting a New Team Member",
        summary: "Add registered SiteMaster users to your organization team.",
        steps: [
          "Important Prerequisite: The individual must first create their own SiteMaster account using their email address before they can be added to your organization",
          "Navigate to the Organization Team section from the left-hand sidebar",
          "Click the + Add Member button",
          "In the Search email field, enter the email address the individual used to register their account",
          "Select their profile from the search results to add them to your team list",
        ],
        tips: [
          "The person must already have a SiteMaster account — they cannot be invited without one",
        ],
      },
      {
        id: "organization-access",
        title: "Assigning Organization Level Access",
        summary: "Set organization-wide permissions for a team member.",
        steps: [
          "In the member's profile row, click the Access Level dropdown under the Organization column",
          "Select Admin to grant them full Admin capabilities across the entire platform",
          "Or select Custom to choose specific organizational modules they can view or edit (e.g., Procurement, Vendor Management)",
        ],
      },
      {
        id: "project-access",
        title: "Assigning Project Level Access",
        summary: "Set project-specific permissions for a team member.",
        steps: [
          "In the member's profile row, locate the specific project under the Project List column",
          "Click the Access Level dropdown next to that project",
          "Select Admin to give them full control over that specific project",
          "Or choose Custom to grant view/edit permissions for specific project modules (e.g., Tasks, Finance, Materials)",
        ],
        tips: [
          "Custom access allows fine-grained control — you can set View or Edit permissions per module for each project",
        ],
      },
    ],
  },
];

export function HelpCenterTab({ onOpenArticle }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const filteredCategories = categories.filter(
    (cat) =>
      cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.articles.some(
        (a) =>
          a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.summary.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
      setFeedback(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  // ── Article View ──────────────────────────────
  if (selectedArticle && selectedCategory) {
    // Separate permission lines from regular steps
    const permissionKeywords = [
      "PERMISSIONS",
      "Full Access",
      "View Only",
      "Auto-Assignment",
      "Assignee Restrictions",
      "Important Prerequisite",
      "Special Condition",
      "Important Condition",
      "Important:",
    ];

    const isPermissionLine = (step) =>
      permissionKeywords.some((kw) => step.startsWith(kw));

    const permissionSteps = selectedArticle.steps.filter(isPermissionLine);
    const regularSteps = selectedArticle.steps.filter(
      (s) => !isPermissionLine(s),
    );

    return (
      <div className="animate-fade-in">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-all duration-200 mb-4 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to {selectedCategory.title}</span>
        </button>

        <article className="max-w-2xl">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            {selectedArticle.title}
          </h2>
          <p className="text-gray-600 mb-6">{selectedArticle.summary}</p>

          {/* Permissions block */}
          {permissionSteps.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-amber-800 mb-2">
                🔒 Permissions & Access
              </p>
              <ul className="space-y-1.5">
                {permissionSteps.map((step, i) => (
                  <li key={i} className="text-sm text-amber-900">
                    • {step.replace(/^PERMISSIONS — /, "")}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Regular steps */}
          <div className="space-y-4 mb-8">
            {regularSteps.map((step, index) => (
              <div
                key={index}
                className="flex gap-4 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-100 text-[#3B0069] flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <p className="text-gray-900 pt-0.5">{step}</p>
              </div>
            ))}
          </div>

          {/* Tips */}
          {selectedArticle.tips && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
              <p className="text-sm font-medium text-blue-900 mb-2">
                💡 Pro Tips
              </p>
              <ul className="space-y-2">
                {selectedArticle.tips.map((tip, index) => (
                  <li key={index} className="text-sm text-gray-700">
                    • {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Feedback */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-sm text-gray-600 mb-3">
              Was this article helpful?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFeedback("up")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                  feedback === "up"
                    ? "bg-green-50 border-green-300 text-green-700"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <ThumbsUp className="h-4 w-4" />
                <span className="text-sm">Yes</span>
              </button>
              <button
                onClick={() => setFeedback("down")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                  feedback === "down"
                    ? "bg-red-50 border-red-300 text-red-700"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <ThumbsDown className="h-4 w-4" />
                <span className="text-sm">No</span>
              </button>
            </div>
            {feedback && (
              <p className="text-sm text-gray-600 mt-3 animate-fade-in">
                Thank you for your feedback! 💛
              </p>
            )}
          </div>
        </article>
      </div>
    );
  }

  // ── Category Articles View ─────────────────────
  if (selectedCategory) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-all duration-200 mb-4 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to all categories</span>
        </button>

        <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-gray-100">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedCategory.colorClass}`}
          >
            <selectedCategory.icon className="h-4 w-4 text-gray-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 leading-tight">
              {selectedCategory.title}
            </h2>
            <p className="text-xs text-gray-500">
              {selectedCategory.description}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {selectedCategory.articles.map((article, index) => (
            <button
              key={article.id}
              onClick={() => setSelectedArticle(article)}
              className="w-full text-left p-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all duration-200 animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <h3 className="font-medium text-gray-900 mb-1">
                {article.title}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {article.summary}
              </p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Main Categories View ───────────────────────
  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search help articles… (e.g., attendance, purchase order, snag)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 h-14 text-base rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category)}
            className={`p-4 rounded-xl border text-left transition-all duration-200 hover:shadow-md animate-fade-in ${category.colorClass}`}
          >
            <category.icon className="h-7 w-7 text-gray-900 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">
              {category.title}
            </h3>
            <p className="text-sm text-gray-600">{category.description}</p>
            <p className="text-xs text-gray-500 mt-2">
              {category.articles.length} article
              {category.articles.length !== 1 ? "s" : ""}
            </p>
          </button>
        ))}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No articles found for "{searchQuery}"</p>
          <p className="text-sm text-gray-500 mt-1">
            Try a different search term
          </p>
        </div>
      )}
    </div>
  );
}
